// FunnelView — ordered funnel stages as CSS horizontal bars, scaled to the max stage.
// Purely presentational: no data fetching, no i18n, no business logic.
// All labels are passed by callers. Uses CSS-only bars (no recharts) for simplicity.
import type { CSSProperties } from 'react';
import { keyColor } from './chartPalette.js';

export interface FunnelStage {
  key: string;
  label: string;
  value: number;
}

export interface FunnelViewProps {
  /** Ordered funnel stages. First stage is typically the largest. */
  stages: FunnelStage[];
  /** Accessible label for the funnel as a whole. */
  ariaLabel: string;
  /** Data table's stage column header. */
  labelHeader: string;
  /** Data table's value column header. */
  valueHeader: string;
}

// Visually hidden but accessible to AT — the deterministic unit-test hook.
const srOnly: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--s-3)',
  fontSize: 'var(--fz-12)',
};

const labelSpanStyle: CSSProperties = {
  flex: 'none',
  width: 120,
  color: 'var(--fg-2)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const trackStyle: CSSProperties = {
  flex: 1,
  height: 16,
  background: 'var(--bg-2)',
  borderRadius: 'var(--r-1)',
  overflow: 'hidden',
};

const valueSpanStyle: CSSProperties = {
  flex: 'none',
  minWidth: 56,
  textAlign: 'right',
  color: 'var(--fg-1)',
  fontVariantNumeric: 'tabular-nums',
};

export function FunnelView({ stages, ariaLabel, labelHeader, valueHeader }: FunnelViewProps) {
  const max = stages.reduce((acc, s) => Math.max(acc, s.value), 0);

  return (
    <div>
      {/* Visual bars — decorative; the data table below is the a11y alternative */}
      <div
        role="img"
        aria-hidden="true"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}
      >
        {stages.map((stage) => {
          const widthPercent = max > 0 ? (stage.value / max) * 100 : 0;
          return (
            <div key={stage.key} style={rowStyle}>
              <span style={labelSpanStyle} title={stage.label}>
                {stage.label}
              </span>
              <span style={trackStyle}>
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    width: `${widthPercent}%`,
                    background: keyColor(stage.key),
                    borderRadius: 'var(--r-1)',
                  }}
                />
              </span>
              <span style={valueSpanStyle}>{stage.value}</span>
            </div>
          );
        })}
      </div>

      {/* Visually-hidden data table: a11y text alternative + test hook */}
      <table style={srOnly} aria-label={ariaLabel}>
        <thead>
          <tr>
            <th scope="col">{labelHeader}</th>
            <th scope="col" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {valueHeader}
            </th>
          </tr>
        </thead>
        <tbody>
          {stages.map((stage) => (
            <tr key={stage.key}>
              <td>{stage.label}</td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{stage.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
